import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import DatePickerShortcuts from 'components/insights/filterBar/DatePickerShortcuts';
import DatePicker from 'components/shared/form/DatePicker';
import useIsMobile from 'hooks/useIsMobile';

import { TaskDateRange } from './taskCards';

type Props = {
  value: TaskDateRange;
  onChange: (range: TaskDateRange) => void;
};

// The header's right-hand range, laid out like InsightsDateRangePicker. Unlike insights the
// range is a filter rather than the page's subject, so it starts empty and either end can be
// cleared to leave that side open — an empty start with an end a week back is "older than a
// week". The draft commits when a calendar closes so typing a date doesn't refetch per
// keystroke.
export default function TasksDateRangePicker({ value, onChange }: Props) {
  const isMobile = useIsMobile();
  const [draft, setDraftState] = useState<TaskDateRange>(value);
  const draftRef = useRef(value);
  const setDraft = (next: TaskDateRange) => {
    draftRef.current = next;
    setDraftState(next);
  };
  useEffect(() => setDraft(value), [value]);

  const commitDraft = () => onChange(draftRef.current);

  const DatePickerFooter = ({ dateInput }: { dateInput: any }) => (
    <DatePickerShortcuts
      startDate={draft.start}
      endDate={draft.end}
      datePickerRef={dateInput}
      setDates={([start, end]: Date[]) => {
        setDraft({ start, end });
        onChange({ start, end });
      }}
    />
  );

  return (
    <Container data-testid='task-created-range'>
      <DatePicker
        data-gtm-id='tasksStartDateFilter'
        data-testid='task-created-start'
        $fullWidth={isMobile}
        selected={draft.start}
        onChange={(val) => setDraft({ ...draftRef.current, start: (val as Date) ?? null })}
        onCalendarClose={commitDraft}
        maxDate={draft.end ?? undefined}
        renderFooter={DatePickerFooter}
        popperPlacement='bottom-start'
      />
      <Emdash>&mdash;</Emdash>
      <DatePicker
        data-gtm-id='tasksEndDateFilter'
        data-testid='task-created-end'
        $fullWidth={isMobile}
        selected={draft.end}
        onChange={(val) => setDraft({ ...draftRef.current, end: (val as Date) ?? null })}
        onCalendarClose={commitDraft}
        minDate={draft.start ?? undefined}
        renderFooter={DatePickerFooter}
        popperPlacement='bottom-start'
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;

  input {
    max-width: 160px;

    @media ${({ theme }) => theme.devices.desktop} {
      max-width: 140px;
    }
  }

  @media (max-width: 800px) {
    width: 100%;

    > div:first-child,
    > div:last-child {
      min-width: 0;
    }

    input {
      max-width: none;
    }
  }
`;

const Emdash = styled.div`
  margin: 0 8px;
`;
